import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconUpload,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import {
  listProductPricing,
  createProductPricing,
  updateProductPricing,
  deleteProductPricing,
  listProductTypes,
  createProductType,
  updateProductType,
  deleteProductType,
  listCategory1,
  createCategory1,
  updateCategory1,
  deleteCategory1,
  listCategory2,
  createCategory2,
  updateCategory2,
  deleteCategory2,
  listCategory3,
  createCategory3,
  updateCategory3,
  deleteCategory3,
  listCategory4,
  createCategory4,
  updateCategory4,
  deleteCategory4,
  importProductPricingFromExcel,
} from '../services/pricingService';
import { parseProductPricingFile } from '../utils/excelImport';
import type {
  ProductPricing,
  ProductType,
  PricingCategory1,
  PricingCategory2,
  PricingCategory3,
  PricingCategory4,
} from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabKey = 'pricing' | 'productTypes' | 'category1' | 'category2' | 'category3' | 'category4';

interface PricingFormState {
  productType: string;
  productTypeId: string;
  designation: string;
  price: string;
  vat: string;
  category1: string;
  category1Id: string;
  category2: string;
  category2Id: string;
  category3: string;
  category3Id: string;
  category4: string;
  category4Id: string;
  notes: string;
  applicableLaw: string;
}

const DEFAULT_PRICING_FORM: PricingFormState = {
  productType: '',
  productTypeId: '',
  designation: '',
  price: '0',
  vat: '0',
  category1: '',
  category1Id: '',
  category2: '',
  category2Id: '',
  category3: '',
  category3Id: '',
  category4: '',
  category4Id: '',
  notes: '',
  applicableLaw: '',
};

interface SimpleFormState {
  name: string;
  order: string;
}

const DEFAULT_SIMPLE_FORM: SimpleFormState = { name: '', order: '0' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProductPricingPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('pricing');

  // -------------------------------------------------------------------------
  // Tab 1 — Pricing
  // -------------------------------------------------------------------------
  const [items, setItems] = useState<ProductPricing[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageHistory, setPageHistory] = useState<(QueryDocumentSnapshot<DocumentData> | null)[]>(
    []
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [category1Filter, setCategory1Filter] = useState('');

  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [allCategory1, setAllCategory1] = useState<PricingCategory1[]>([]);
  const [allCategory2, setAllCategory2] = useState<PricingCategory2[]>([]);
  const [allCategory3, setAllCategory3] = useState<PricingCategory3[]>([]);
  const [allCategory4, setAllCategory4] = useState<PricingCategory4[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ProductPricing | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<PricingFormState>(DEFAULT_PRICING_FORM);

  const [importing, setImporting] = useState(false);

  // Load product types and all categories on mount for filter and modal dropdowns
  useEffect(() => {
    listProductTypes()
      .then(data => {
        setProductTypes(data);
        setPtItems(data);
        ptLoaded.current = true;
      })
      .catch(err => console.error('Failed to load product types:', err));
    listCategory1()
      .then(data => {
        setAllCategory1(data);
        setC1Items(data);
        c1Loaded.current = true;
      })
      .catch(err => console.error('Failed to load category 1:', err));
    listCategory2()
      .then(data => {
        setAllCategory2(data);
        setC2Items(data);
        c2Loaded.current = true;
      })
      .catch(err => console.error('Failed to load category 2:', err));
    listCategory3()
      .then(data => {
        setAllCategory3(data);
        setC3Items(data);
        c3Loaded.current = true;
      })
      .catch(err => console.error('Failed to load category 3:', err));
    listCategory4()
      .then(data => {
        setAllCategory4(data);
        setC4Items(data);
        c4Loaded.current = true;
      })
      .catch(err => console.error('Failed to load category 4:', err));
  }, []);

  // Reload pricing on filter change
  const loadPricing = useCallback(
    async (cursor: QueryDocumentSnapshot<DocumentData> | null) => {
      setLoading(true);
      try {
        const result = await listProductPricing({
          pageSize: 50,
          lastDoc: cursor,
          searchQuery,
          productTypeId: productTypeFilter,
          category1Id: category1Filter,
        });
        setItems(result.items);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error('Failed to load pricing:', err);
        alert('Failed to load pricing data.');
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, productTypeFilter, category1Filter]
  );

  useEffect(() => {
    setLastDoc(null);
    setItems([]);
    setHasMore(false);
    setPageHistory([]);
    loadPricing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, productTypeFilter, category1Filter]);

  function handleNextPage() {
    setPageHistory(h => [...h, lastDoc]);
    loadPricing(lastDoc);
  }

  function handlePreviousPage() {
    const prev = pageHistory[pageHistory.length - 1] ?? null;
    setPageHistory(h => h.slice(0, -1));
    loadPricing(prev);
  }

  const currentPage = pageHistory.length + 1;

  function openAddPricing() {
    setEditing(null);
    setForm(DEFAULT_PRICING_FORM);
    setFormError(null);
    setShowModal(true);
  }

  function openEditPricing(item: ProductPricing) {
    setEditing(item);
    setForm({
      productType: item.productType ?? '',
      productTypeId: item.productTypeId ?? '',
      designation: item.designation ?? '',
      price: item.price !== undefined ? String(item.price) : '0',
      vat: item.vat !== undefined ? String(item.vat) : '0',
      category1: item.category1 ?? '',
      category1Id: item.category1Id ?? '',
      category2: item.category2 ?? '',
      category2Id: item.category2Id ?? '',
      category3: item.category3 ?? '',
      category3Id: item.category3Id ?? '',
      category4: item.category4 ?? '',
      category4Id: item.category4Id ?? '',
      notes: item.notes ?? '',
      applicableLaw: item.applicableLaw ?? '',
    });
    setFormError(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setFormError(null);
  }

  async function handleSave() {
    if (!form.designation.trim()) {
      setFormError('Designation is required.');
      return;
    }
    if (!form.productTypeId.trim()) {
      setFormError('Product Type is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload: Omit<ProductPricing, 'id'> = {
        productType: form.productType,
        productTypeId: form.productTypeId,
        designation: form.designation.trim(),
        price: parseFloat(form.price) || 0,
        vat: parseFloat(form.vat) || 0,
        ...(form.category1Id ? { category1: form.category1, category1Id: form.category1Id } : {}),
        ...(form.category2Id ? { category2: form.category2, category2Id: form.category2Id } : {}),
        ...(form.category3Id ? { category3: form.category3, category3Id: form.category3Id } : {}),
        ...(form.category4Id ? { category4: form.category4, category4Id: form.category4Id } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        ...(form.applicableLaw.trim() ? { applicableLaw: form.applicableLaw.trim() } : {}),
        searchKeywords: [],
      };
      if (editing) {
        await updateProductPricing(editing.id, payload);
      } else {
        await createProductPricing(payload);
      }
      closeModal();
      setPageHistory([]);
      await loadPricing(null);
    } catch (err) {
      console.error('Failed to save pricing item:', err);
      setFormError('Failed to save pricing item. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: ProductPricing) {
    if (!confirm(`Delete pricing item "${item.designation}"?`)) return;
    try {
      await deleteProductPricing(item.id);
      setItems(prev => prev.filter(p => p.id !== item.id));
    } catch (err) {
      console.error('Failed to delete pricing item:', err);
      alert('Failed to delete pricing item.');
    }
  }

  async function handlePricingExcelImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';
    setImporting(true);
    try {
      const parsed = await parseProductPricingFile(file);
      await importProductPricingFromExcel(parsed);
      // Reload product types for filter dropdowns
      const updatedTypes = await listProductTypes();
      setProductTypes(updatedTypes);
      setPageHistory([]);
      await loadPricing(null);
      alert(`Import successful: ${parsed.pricingItems.length} pricing items imported.`);
    } catch (err) {
      console.error('Import failed:', err);
      alert('Import failed. Please check the file and try again.');
    } finally {
      setImporting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Tab 2 — Product Types
  // -------------------------------------------------------------------------
  const [ptItems, setPtItems] = useState<ProductType[]>([]);
  const [ptLoading, setPtLoading] = useState(false);
  const [showPtModal, setShowPtModal] = useState(false);
  const [editingPt, setEditingPt] = useState<ProductType | null>(null);
  const [ptForm, setPtForm] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [ptSaving, setPtSaving] = useState(false);
  const [ptError, setPtError] = useState<string | null>(null);
  const [ptLinkedCat1Ids, setPtLinkedCat1Ids] = useState<string[]>([]);
  const ptLoaded = useRef(false);

  async function loadPtItems() {
    setPtLoading(true);
    try {
      const data = await listProductTypes();
      setPtItems(data);
      setProductTypes(data);
    } catch (err) {
      console.error('Failed to load product types:', err);
      alert('Failed to load product types.');
    } finally {
      setPtLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'productTypes' && !ptLoaded.current) {
      ptLoaded.current = true;
      loadPtItems();
    }
  }, [activeTab]);

  function openAddPt() {
    setEditingPt(null);
    setPtForm(DEFAULT_SIMPLE_FORM);
    setPtLinkedCat1Ids([]);
    setPtError(null);
    setShowPtModal(true);
  }

  function openEditPt(item: ProductType) {
    setEditingPt(item);
    setPtForm({ name: item.name, order: String(item.order) });
    setPtLinkedCat1Ids(item.linkedCategory1Ids ?? []);
    setPtError(null);
    setShowPtModal(true);
  }

  function closePtModal() {
    setShowPtModal(false);
    setEditingPt(null);
    setPtError(null);
  }

  async function handlePtSave() {
    if (!ptForm.name.trim()) {
      setPtError('Name is required.');
      return;
    }
    if (!ptForm.order.trim()) {
      setPtError('Order is required.');
      return;
    }
    setPtSaving(true);
    setPtError(null);
    try {
      const payload = {
        name: ptForm.name.trim(),
        order: parseInt(ptForm.order, 10) || 0,
        linkedCategory1Ids: ptLinkedCat1Ids,
      };
      if (editingPt) {
        await updateProductType(editingPt.id, payload);
      } else {
        await createProductType(payload);
      }
      closePtModal();
      await loadPtItems();
    } catch (err) {
      console.error('Failed to save product type:', err);
      setPtError('Failed to save product type. Please try again.');
    } finally {
      setPtSaving(false);
    }
  }

  async function handlePtDelete(item: ProductType) {
    if (!confirm(`Delete product type "${item.name}"?`)) return;
    try {
      await deleteProductType(item.id);
      setPtItems(prev => prev.filter(p => p.id !== item.id));
    } catch (err) {
      console.error('Failed to delete product type:', err);
      alert('Failed to delete product type.');
    }
  }

  // -------------------------------------------------------------------------
  // Tab 3 — Category 1
  // -------------------------------------------------------------------------
  const [c1Items, setC1Items] = useState<PricingCategory1[]>([]);
  const [c1Loading, setC1Loading] = useState(false);
  const [showC1Modal, setShowC1Modal] = useState(false);
  const [editingC1, setEditingC1] = useState<PricingCategory1 | null>(null);
  const [c1Form, setC1Form] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [c1Saving, setC1Saving] = useState(false);
  const [c1Error, setC1Error] = useState<string | null>(null);
  const [c1LinkedCat2Ids, setC1LinkedCat2Ids] = useState<string[]>([]);
  const c1Loaded = useRef(false);

  async function loadC1Items() {
    setC1Loading(true);
    try {
      const data = await listCategory1();
      setC1Items(data);
      setAllCategory1(data);
    } catch (err) {
      console.error('Failed to load category 1:', err);
      alert('Failed to load Category 1.');
    } finally {
      setC1Loading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'category1' && !c1Loaded.current) {
      c1Loaded.current = true;
      loadC1Items();
    }
  }, [activeTab]);

  function openAddC1() {
    setEditingC1(null);
    setC1Form(DEFAULT_SIMPLE_FORM);
    setC1LinkedCat2Ids([]);
    setC1Error(null);
    setShowC1Modal(true);
  }

  function openEditC1(item: PricingCategory1) {
    setEditingC1(item);
    setC1Form({ name: item.name, order: String(item.order) });
    setC1LinkedCat2Ids(item.linkedCategory2Ids ?? []);
    setC1Error(null);
    setShowC1Modal(true);
  }

  function closeC1Modal() {
    setShowC1Modal(false);
    setEditingC1(null);
    setC1Error(null);
  }

  async function handleC1Save() {
    if (!c1Form.name.trim()) {
      setC1Error('Name is required.');
      return;
    }
    if (!c1Form.order.trim()) {
      setC1Error('Order is required.');
      return;
    }
    setC1Saving(true);
    setC1Error(null);
    try {
      const payload = {
        name: c1Form.name.trim(),
        order: parseInt(c1Form.order, 10) || 0,
        linkedCategory2Ids: c1LinkedCat2Ids,
      };
      if (editingC1) {
        await updateCategory1(editingC1.id, payload);
      } else {
        await createCategory1(payload);
      }
      closeC1Modal();
      await loadC1Items();
    } catch (err) {
      console.error('Failed to save category 1:', err);
      setC1Error('Failed to save category 1. Please try again.');
    } finally {
      setC1Saving(false);
    }
  }

  async function handleC1Delete(item: PricingCategory1) {
    if (!confirm(`Delete category 1 "${item.name}"?`)) return;
    try {
      await deleteCategory1(item.id);
      setC1Items(prev => prev.filter(c => c.id !== item.id));
    } catch (err) {
      console.error('Failed to delete category 1:', err);
      alert('Failed to delete category 1.');
    }
  }

  // -------------------------------------------------------------------------
  // Tab 4 — Category 2
  // -------------------------------------------------------------------------
  const [c2Items, setC2Items] = useState<PricingCategory2[]>([]);
  const [c2Loading, setC2Loading] = useState(false);
  const [showC2Modal, setShowC2Modal] = useState(false);
  const [editingC2, setEditingC2] = useState<PricingCategory2 | null>(null);
  const [c2Form, setC2Form] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [c2Saving, setC2Saving] = useState(false);
  const [c2Error, setC2Error] = useState<string | null>(null);
  const [c2LinkedCat3Ids, setC2LinkedCat3Ids] = useState<string[]>([]);
  const c2Loaded = useRef(false);

  async function loadC2Items() {
    setC2Loading(true);
    try {
      const data = await listCategory2();
      setC2Items(data);
      setAllCategory2(data);
    } catch (err) {
      console.error('Failed to load category 2:', err);
      alert('Failed to load Category 2.');
    } finally {
      setC2Loading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'category2' && !c2Loaded.current) {
      c2Loaded.current = true;
      loadC2Items();
    }
  }, [activeTab]);

  function openAddC2() {
    setEditingC2(null);
    setC2Form(DEFAULT_SIMPLE_FORM);
    setC2LinkedCat3Ids([]);
    setC2Error(null);
    setShowC2Modal(true);
  }

  function openEditC2(item: PricingCategory2) {
    setEditingC2(item);
    setC2Form({ name: item.name, order: String(item.order) });
    setC2LinkedCat3Ids(item.linkedCategory3Ids ?? []);
    setC2Error(null);
    setShowC2Modal(true);
  }

  function closeC2Modal() {
    setShowC2Modal(false);
    setEditingC2(null);
    setC2Error(null);
  }

  async function handleC2Save() {
    if (!c2Form.name.trim()) {
      setC2Error('Name is required.');
      return;
    }
    if (!c2Form.order.trim()) {
      setC2Error('Order is required.');
      return;
    }
    setC2Saving(true);
    setC2Error(null);
    try {
      const payload = {
        name: c2Form.name.trim(),
        order: parseInt(c2Form.order, 10) || 0,
        linkedCategory3Ids: c2LinkedCat3Ids,
      };
      if (editingC2) {
        await updateCategory2(editingC2.id, payload);
      } else {
        await createCategory2(payload);
      }
      closeC2Modal();
      await loadC2Items();
    } catch (err) {
      console.error('Failed to save category 2:', err);
      setC2Error('Failed to save category 2. Please try again.');
    } finally {
      setC2Saving(false);
    }
  }

  async function handleC2Delete(item: PricingCategory2) {
    if (!confirm(`Delete category 2 "${item.name}"?`)) return;
    try {
      await deleteCategory2(item.id);
      setC2Items(prev => prev.filter(c => c.id !== item.id));
    } catch (err) {
      console.error('Failed to delete category 2:', err);
      alert('Failed to delete category 2.');
    }
  }

  // -------------------------------------------------------------------------
  // Tab 5 — Category 3
  // -------------------------------------------------------------------------
  const [c3Items, setC3Items] = useState<PricingCategory3[]>([]);
  const [c3Loading, setC3Loading] = useState(false);
  const [showC3Modal, setShowC3Modal] = useState(false);
  const [editingC3, setEditingC3] = useState<PricingCategory3 | null>(null);
  const [c3Form, setC3Form] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [c3Saving, setC3Saving] = useState(false);
  const [c3Error, setC3Error] = useState<string | null>(null);
  const [c3LinkedCat4Ids, setC3LinkedCat4Ids] = useState<string[]>([]);
  const c3Loaded = useRef(false);

  async function loadC3Items() {
    setC3Loading(true);
    try {
      const data = await listCategory3();
      setC3Items(data);
      setAllCategory3(data);
    } catch (err) {
      console.error('Failed to load category 3:', err);
      alert('Failed to load Category 3.');
    } finally {
      setC3Loading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'category3' && !c3Loaded.current) {
      c3Loaded.current = true;
      loadC3Items();
    }
  }, [activeTab]);

  function openAddC3() {
    setEditingC3(null);
    setC3Form(DEFAULT_SIMPLE_FORM);
    setC3LinkedCat4Ids([]);
    setC3Error(null);
    setShowC3Modal(true);
  }

  function openEditC3(item: PricingCategory3) {
    setEditingC3(item);
    setC3Form({ name: item.name, order: String(item.order) });
    setC3LinkedCat4Ids(item.linkedCategory4Ids ?? []);
    setC3Error(null);
    setShowC3Modal(true);
  }

  function closeC3Modal() {
    setShowC3Modal(false);
    setEditingC3(null);
    setC3Error(null);
  }

  async function handleC3Save() {
    if (!c3Form.name.trim()) {
      setC3Error('Name is required.');
      return;
    }
    if (!c3Form.order.trim()) {
      setC3Error('Order is required.');
      return;
    }
    setC3Saving(true);
    setC3Error(null);
    try {
      const payload = {
        name: c3Form.name.trim(),
        order: parseInt(c3Form.order, 10) || 0,
        linkedCategory4Ids: c3LinkedCat4Ids,
      };
      if (editingC3) {
        await updateCategory3(editingC3.id, payload);
      } else {
        await createCategory3(payload);
      }
      closeC3Modal();
      await loadC3Items();
    } catch (err) {
      console.error('Failed to save category 3:', err);
      setC3Error('Failed to save category 3. Please try again.');
    } finally {
      setC3Saving(false);
    }
  }

  async function handleC3Delete(item: PricingCategory3) {
    if (!confirm(`Delete category 3 "${item.name}"?`)) return;
    try {
      await deleteCategory3(item.id);
      setC3Items(prev => prev.filter(c => c.id !== item.id));
    } catch (err) {
      console.error('Failed to delete category 3:', err);
      alert('Failed to delete category 3.');
    }
  }

  // -------------------------------------------------------------------------
  // Tab 6 — Category 4
  // -------------------------------------------------------------------------
  const [c4Items, setC4Items] = useState<PricingCategory4[]>([]);
  const [c4Loading, setC4Loading] = useState(false);
  const [showC4Modal, setShowC4Modal] = useState(false);
  const [editingC4, setEditingC4] = useState<PricingCategory4 | null>(null);
  const [c4Form, setC4Form] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [c4Saving, setC4Saving] = useState(false);
  const [c4Error, setC4Error] = useState<string | null>(null);
  const c4Loaded = useRef(false);

  async function loadC4Items() {
    setC4Loading(true);
    try {
      const data = await listCategory4();
      setC4Items(data);
      setAllCategory4(data);
    } catch (err) {
      console.error('Failed to load category 4:', err);
      alert('Failed to load Category 4.');
    } finally {
      setC4Loading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'category4' && !c4Loaded.current) {
      c4Loaded.current = true;
      loadC4Items();
    }
  }, [activeTab]);

  function openAddC4() {
    setEditingC4(null);
    setC4Form(DEFAULT_SIMPLE_FORM);
    setC4Error(null);
    setShowC4Modal(true);
  }

  function openEditC4(item: PricingCategory4) {
    setEditingC4(item);
    setC4Form({ name: item.name, order: String(item.order) });
    setC4Error(null);
    setShowC4Modal(true);
  }

  function closeC4Modal() {
    setShowC4Modal(false);
    setEditingC4(null);
    setC4Error(null);
  }

  async function handleC4Save() {
    if (!c4Form.name.trim()) {
      setC4Error('Name is required.');
      return;
    }
    if (!c4Form.order.trim()) {
      setC4Error('Order is required.');
      return;
    }
    setC4Saving(true);
    setC4Error(null);
    try {
      const payload = {
        name: c4Form.name.trim(),
        order: parseInt(c4Form.order, 10) || 0,
      };
      if (editingC4) {
        await updateCategory4(editingC4.id, payload);
      } else {
        await createCategory4(payload);
      }
      closeC4Modal();
      await loadC4Items();
    } catch (err) {
      console.error('Failed to save category 4:', err);
      setC4Error('Failed to save category 4. Please try again.');
    } finally {
      setC4Saving(false);
    }
  }

  async function handleC4Delete(item: PricingCategory4) {
    if (!confirm(`Delete category 4 "${item.name}"?`)) return;
    try {
      await deleteCategory4(item.id);
      setC4Items(prev => prev.filter(c => c.id !== item.id));
    } catch (err) {
      console.error('Failed to delete category 4:', err);
      alert('Failed to delete category 4.');
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const filteredCategory1 = form.productTypeId
    ? allCategory1.filter(
        c =>
          productTypes
            .find(pt => pt.id === form.productTypeId)
            ?.linkedCategory1Ids?.includes(c.id) ?? true
      )
    : allCategory1;

  const filteredCategory2 = form.category1Id
    ? allCategory2.filter(
        c =>
          allCategory1.find(c1 => c1.id === form.category1Id)?.linkedCategory2Ids?.includes(c.id) ??
          true
      )
    : allCategory2;

  const filteredCategory3 = form.category2Id
    ? allCategory3.filter(
        c =>
          allCategory2.find(c2 => c2.id === form.category2Id)?.linkedCategory3Ids?.includes(c.id) ??
          true
      )
    : allCategory3;

  const filteredCategory4 = form.category3Id
    ? allCategory4.filter(
        c =>
          allCategory3.find(c3 => c3.id === form.category3Id)?.linkedCategory4Ids?.includes(c.id) ??
          true
      )
    : allCategory4;

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Market</div>
          <h2 className="page-title">Product Pricing</h2>
        </div>
        {activeTab === 'pricing' && (
          <div className="col-auto d-flex gap-2">
            {/* Excel Import */}
            {importing ? (
              <button className="btn btn-outline-secondary" disabled>
                <span className="spinner-border spinner-border-sm me-1" />
                Importing…
              </button>
            ) : (
              <label className="btn btn-outline-secondary" style={{ cursor: 'pointer' }}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handlePricingExcelImport}
                />
                <IconUpload className="icon me-1" />
                Import Excel
              </label>
            )}
            <button className="btn btn-primary" onClick={openAddPricing}>
              <IconPlus className="icon" />
              Add Pricing
            </button>
          </div>
        )}
        {activeTab === 'productTypes' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openAddPt}>
              <IconPlus className="icon" />
              Add Product Type
            </button>
          </div>
        )}
        {activeTab === 'category1' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openAddC1}>
              <IconPlus className="icon" />
              Add Category 1
            </button>
          </div>
        )}
        {activeTab === 'category2' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openAddC2}>
              <IconPlus className="icon" />
              Add Category 2
            </button>
          </div>
        )}
        {activeTab === 'category3' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openAddC3}>
              <IconPlus className="icon" />
              Add Category 3
            </button>
          </div>
        )}
        {activeTab === 'category4' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openAddC4}>
              <IconPlus className="icon" />
              Add Category 4
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3 mt-3">
        <li className="nav-item">
          <a
            className={`nav-link${activeTab === 'pricing' ? ' active' : ''}`}
            href="#"
            onClick={e => {
              e.preventDefault();
              setActiveTab('pricing');
            }}
          >
            Pricing
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link${activeTab === 'productTypes' ? ' active' : ''}`}
            href="#"
            onClick={e => {
              e.preventDefault();
              setActiveTab('productTypes');
            }}
          >
            Product Types
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link${activeTab === 'category1' ? ' active' : ''}`}
            href="#"
            onClick={e => {
              e.preventDefault();
              setActiveTab('category1');
            }}
          >
            Category 1
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link${activeTab === 'category2' ? ' active' : ''}`}
            href="#"
            onClick={e => {
              e.preventDefault();
              setActiveTab('category2');
            }}
          >
            Category 2
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link${activeTab === 'category3' ? ' active' : ''}`}
            href="#"
            onClick={e => {
              e.preventDefault();
              setActiveTab('category3');
            }}
          >
            Category 3
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link${activeTab === 'category4' ? ' active' : ''}`}
            href="#"
            onClick={e => {
              e.preventDefault();
              setActiveTab('category4');
            }}
          >
            Category 4
          </a>
        </li>
      </ul>

      {/* ------------------------------------------------------------------ */}
      {/* Tab 1 — Pricing                                                     */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'pricing' && (
        <>
          {/* Filters */}
          <div className="card mt-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-12 col-md-5">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by designation…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Product Type</label>
                  <select
                    className="form-select"
                    value={productTypeFilter}
                    onChange={e => setProductTypeFilter(e.target.value)}
                  >
                    <option value="">All Product Types</option>
                    {productTypes.map(pt => (
                      <option key={pt.id} value={pt.id}>
                        {pt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Category 1</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filter by category 1 ID…"
                    value={category1Filter}
                    onChange={e => setCategory1Filter(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Designation</th>
                    <th>Product Type</th>
                    <th>Cat 1</th>
                    <th>Cat 2</th>
                    <th>Price</th>
                    <th>VAT %</th>
                    <th>Notes</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="empty">
                          <p className="empty-title">No pricing items found</p>
                          <p className="empty-subtitle text-muted">
                            Try adjusting your filters or import an Excel file
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.map(item => (
                      <tr key={item.id}>
                        <td>{item.designation}</td>
                        <td>{item.productType || '—'}</td>
                        <td>{item.category1 || '—'}</td>
                        <td>{item.category2 || '—'}</td>
                        <td>{item.price !== undefined ? item.price.toFixed(2) : '—'}</td>
                        <td>{item.vat !== undefined ? item.vat : '—'}</td>
                        <td>
                          {item.notes
                            ? item.notes.length > 40
                              ? item.notes.slice(0, 40) + '…'
                              : item.notes
                            : '—'}
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEditPricing(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleDelete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-footer d-flex align-items-center">
              <p className="m-0 text-muted">Page {currentPage}</p>
              <ul className="pagination m-0 ms-auto">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <IconChevronLeft size={16} />
                    prev
                  </button>
                </li>
                <li className={`page-item ${!hasMore ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNextPage} disabled={!hasMore}>
                    next
                    <IconChevronRight size={16} />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Tab 2 — Product Types                                               */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'productTypes' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Linked Cat 1 Count</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ptLoading && ptItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : ptItems.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty">
                          <p className="empty-title">No product types found</p>
                          <p className="empty-subtitle text-muted">
                            Add a product type to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    ptItems.map(item => (
                      <tr key={item.id}>
                        <td className="fw-bold">{item.name}</td>
                        <td>{item.order}</td>
                        <td>
                          <span className="badge bg-blue-lt">
                            {item.linkedCategory1Ids?.length ?? 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEditPt(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handlePtDelete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Tab 3 — Category 1                                                  */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'category1' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Linked Cat 2 Count</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {c1Loading && c1Items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : c1Items.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty">
                          <p className="empty-title">No category 1 items found</p>
                          <p className="empty-subtitle text-muted">
                            Add a category 1 item to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    c1Items.map(item => (
                      <tr key={item.id}>
                        <td className="fw-bold">{item.name}</td>
                        <td>{item.order}</td>
                        <td>
                          <span className="badge bg-blue-lt">
                            {item.linkedCategory2Ids?.length ?? 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEditC1(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleC1Delete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Tab 4 — Category 2                                                  */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'category2' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Linked Cat 3 Count</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {c2Loading && c2Items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : c2Items.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty">
                          <p className="empty-title">No category 2 items found</p>
                          <p className="empty-subtitle text-muted">
                            Add a category 2 item to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    c2Items.map(item => (
                      <tr key={item.id}>
                        <td className="fw-bold">{item.name}</td>
                        <td>{item.order}</td>
                        <td>
                          <span className="badge bg-blue-lt">
                            {item.linkedCategory3Ids?.length ?? 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEditC2(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleC2Delete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Tab 5 — Category 3                                                  */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'category3' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Linked Cat 4 Count</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {c3Loading && c3Items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : c3Items.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty">
                          <p className="empty-title">No category 3 items found</p>
                          <p className="empty-subtitle text-muted">
                            Add a category 3 item to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    c3Items.map(item => (
                      <tr key={item.id}>
                        <td className="fw-bold">{item.name}</td>
                        <td>{item.order}</td>
                        <td>
                          <span className="badge bg-blue-lt">
                            {item.linkedCategory4Ids?.length ?? 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEditC3(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleC3Delete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Tab 6 — Category 4                                                  */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'category4' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {c4Loading && c4Items.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : c4Items.length === 0 ? (
                    <tr>
                      <td colSpan={3}>
                        <div className="empty">
                          <p className="empty-title">No category 4 items found</p>
                          <p className="empty-subtitle text-muted">
                            Add a category 4 item to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    c4Items.map(item => (
                      <tr key={item.id}>
                        <td className="fw-bold">{item.name}</td>
                        <td>{item.order}</td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEditC4(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleC4Delete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ================================================================== */}
      {/* Pricing Modal                                                        */}
      {/* ================================================================== */}
      {showModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editing ? 'Edit Pricing Item' : 'Add Pricing Item'}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger mb-3">{formError}</div>}
                <div className="row g-3">
                  {/* Product Type */}
                  <div className="col-12">
                    <label className="form-label required">Product Type</label>
                    <select
                      className="form-select"
                      value={form.productTypeId}
                      onChange={e => {
                        const selected = productTypes.find(pt => pt.id === e.target.value);
                        setForm(prev => ({
                          ...prev,
                          productTypeId: e.target.value,
                          productType: selected?.name ?? '',
                          category1Id: '',
                          category1: '',
                          category2Id: '',
                          category2: '',
                          category3Id: '',
                          category3: '',
                          category4Id: '',
                          category4: '',
                        }));
                      }}
                    >
                      <option value="">— Select Product Type —</option>
                      {productTypes.map(pt => (
                        <option key={pt.id} value={pt.id}>
                          {pt.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Designation */}
                  <div className="col-12">
                    <label className="form-label required">Designation</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.designation}
                      onChange={e => setForm(prev => ({ ...prev, designation: e.target.value }))}
                      placeholder="Item designation"
                    />
                  </div>

                  {/* Price + VAT */}
                  <div className="col-md-6">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.price}
                      onChange={e => setForm(prev => ({ ...prev, price: e.target.value }))}
                      min={0}
                      step={0.01}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">VAT %</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.vat}
                      onChange={e => setForm(prev => ({ ...prev, vat: e.target.value }))}
                      min={0}
                      max={100}
                      step={1}
                      placeholder="0"
                    />
                  </div>

                  {/* Category 1 */}
                  <div className="col-12">
                    <label className="form-label">Category 1</label>
                    <select
                      className="form-select"
                      value={form.category1Id}
                      onChange={e => {
                        const selected = allCategory1.find(c => c.id === e.target.value);
                        setForm(prev => ({
                          ...prev,
                          category1Id: e.target.value,
                          category1: selected?.name ?? '',
                          category2Id: '',
                          category2: '',
                          category3Id: '',
                          category3: '',
                          category4Id: '',
                          category4: '',
                        }));
                      }}
                    >
                      <option value="">— None —</option>
                      {filteredCategory1.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category 2 */}
                  <div className="col-12">
                    <label className="form-label">Category 2</label>
                    <select
                      className="form-select"
                      value={form.category2Id}
                      disabled={!form.category1Id}
                      onChange={e => {
                        const selected = allCategory2.find(c => c.id === e.target.value);
                        setForm(prev => ({
                          ...prev,
                          category2Id: e.target.value,
                          category2: selected?.name ?? '',
                          category3Id: '',
                          category3: '',
                          category4Id: '',
                          category4: '',
                        }));
                      }}
                    >
                      <option value="">— None —</option>
                      {filteredCategory2.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category 3 */}
                  <div className="col-12">
                    <label className="form-label">Category 3</label>
                    <select
                      className="form-select"
                      value={form.category3Id}
                      disabled={!form.category2Id}
                      onChange={e => {
                        const selected = allCategory3.find(c => c.id === e.target.value);
                        setForm(prev => ({
                          ...prev,
                          category3Id: e.target.value,
                          category3: selected?.name ?? '',
                          category4Id: '',
                          category4: '',
                        }));
                      }}
                    >
                      <option value="">— None —</option>
                      {filteredCategory3.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category 4 */}
                  <div className="col-12">
                    <label className="form-label">Category 4</label>
                    <select
                      className="form-select"
                      value={form.category4Id}
                      disabled={!form.category3Id}
                      onChange={e => {
                        const selected = allCategory4.find(c => c.id === e.target.value);
                        setForm(prev => ({
                          ...prev,
                          category4Id: e.target.value,
                          category4: selected?.name ?? '',
                        }));
                      }}
                    >
                      <option value="">— None —</option>
                      {filteredCategory4.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Notes */}
                  <div className="col-12">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      value={form.notes}
                      onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      placeholder="Optional notes…"
                    />
                  </div>

                  {/* Applicable Law */}
                  <div className="col-12">
                    <label className="form-label">Applicable Law</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.applicableLaw}
                      onChange={e => setForm(prev => ({ ...prev, applicableLaw: e.target.value }))}
                      placeholder="Optional legal reference…"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editing ? (
                    'Save Changes'
                  ) : (
                    'Add Pricing Item'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Product Types Modal                                                  */}
      {/* ================================================================== */}
      {showPtModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingPt ? 'Edit Product Type' : 'Add Product Type'}
                </h5>
                <button type="button" className="btn-close" onClick={closePtModal} />
              </div>
              <div className="modal-body">
                {ptError && <div className="alert alert-danger mb-3">{ptError}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={ptForm.name}
                      onChange={e => setPtForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Product type name"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={ptForm.order}
                      onChange={e => setPtForm(prev => ({ ...prev, order: e.target.value }))}
                      min={0}
                      step={1}
                      placeholder="0"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Linked Category 1 Items</label>
                    {allCategory1.length === 0 ? (
                      <p className="text-muted small">No category 1 items available yet.</p>
                    ) : (
                      <div className="row g-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {allCategory1.map(c => (
                          <div className="col-6" key={c.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={ptLinkedCat1Ids.includes(c.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setPtLinkedCat1Ids(prev => [...prev, c.id]);
                                  } else {
                                    setPtLinkedCat1Ids(prev => prev.filter(id => id !== c.id));
                                  }
                                }}
                              />
                              <span className="form-check-label">{c.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closePtModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePtSave}
                  disabled={ptSaving}
                >
                  {ptSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingPt ? (
                    'Save Changes'
                  ) : (
                    'Add Product Type'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Category 1 Modal                                                     */}
      {/* ================================================================== */}
      {showC1Modal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingC1 ? 'Edit Category 1' : 'Add Category 1'}</h5>
                <button type="button" className="btn-close" onClick={closeC1Modal} />
              </div>
              <div className="modal-body">
                {c1Error && <div className="alert alert-danger mb-3">{c1Error}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={c1Form.name}
                      onChange={e => setC1Form(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Category 1 name"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={c1Form.order}
                      onChange={e => setC1Form(prev => ({ ...prev, order: e.target.value }))}
                      min={0}
                      step={1}
                      placeholder="0"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Linked Category 2 Items</label>
                    {allCategory2.length === 0 ? (
                      <p className="text-muted small">No category 2 items available yet.</p>
                    ) : (
                      <div className="row g-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {allCategory2.map(c => (
                          <div className="col-6" key={c.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={c1LinkedCat2Ids.includes(c.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setC1LinkedCat2Ids(prev => [...prev, c.id]);
                                  } else {
                                    setC1LinkedCat2Ids(prev => prev.filter(id => id !== c.id));
                                  }
                                }}
                              />
                              <span className="form-check-label">{c.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeC1Modal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleC1Save}
                  disabled={c1Saving}
                >
                  {c1Saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingC1 ? (
                    'Save Changes'
                  ) : (
                    'Add Category 1'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Category 2 Modal                                                     */}
      {/* ================================================================== */}
      {showC2Modal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingC2 ? 'Edit Category 2' : 'Add Category 2'}</h5>
                <button type="button" className="btn-close" onClick={closeC2Modal} />
              </div>
              <div className="modal-body">
                {c2Error && <div className="alert alert-danger mb-3">{c2Error}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={c2Form.name}
                      onChange={e => setC2Form(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Category 2 name"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={c2Form.order}
                      onChange={e => setC2Form(prev => ({ ...prev, order: e.target.value }))}
                      min={0}
                      step={1}
                      placeholder="0"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Linked Category 3 Items</label>
                    {allCategory3.length === 0 ? (
                      <p className="text-muted small">No category 3 items available yet.</p>
                    ) : (
                      <div className="row g-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {allCategory3.map(c => (
                          <div className="col-6" key={c.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={c2LinkedCat3Ids.includes(c.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setC2LinkedCat3Ids(prev => [...prev, c.id]);
                                  } else {
                                    setC2LinkedCat3Ids(prev => prev.filter(id => id !== c.id));
                                  }
                                }}
                              />
                              <span className="form-check-label">{c.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeC2Modal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleC2Save}
                  disabled={c2Saving}
                >
                  {c2Saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingC2 ? (
                    'Save Changes'
                  ) : (
                    'Add Category 2'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Category 3 Modal                                                     */}
      {/* ================================================================== */}
      {showC3Modal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingC3 ? 'Edit Category 3' : 'Add Category 3'}</h5>
                <button type="button" className="btn-close" onClick={closeC3Modal} />
              </div>
              <div className="modal-body">
                {c3Error && <div className="alert alert-danger mb-3">{c3Error}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={c3Form.name}
                      onChange={e => setC3Form(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Category 3 name"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={c3Form.order}
                      onChange={e => setC3Form(prev => ({ ...prev, order: e.target.value }))}
                      min={0}
                      step={1}
                      placeholder="0"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Linked Category 4 Items</label>
                    {allCategory4.length === 0 ? (
                      <p className="text-muted small">No category 4 items available yet.</p>
                    ) : (
                      <div className="row g-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {allCategory4.map(c => (
                          <div className="col-6" key={c.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={c3LinkedCat4Ids.includes(c.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setC3LinkedCat4Ids(prev => [...prev, c.id]);
                                  } else {
                                    setC3LinkedCat4Ids(prev => prev.filter(id => id !== c.id));
                                  }
                                }}
                              />
                              <span className="form-check-label">{c.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeC3Modal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleC3Save}
                  disabled={c3Saving}
                >
                  {c3Saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingC3 ? (
                    'Save Changes'
                  ) : (
                    'Add Category 3'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Category 4 Modal                                                     */}
      {/* ================================================================== */}
      {showC4Modal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingC4 ? 'Edit Category 4' : 'Add Category 4'}</h5>
                <button type="button" className="btn-close" onClick={closeC4Modal} />
              </div>
              <div className="modal-body">
                {c4Error && <div className="alert alert-danger mb-3">{c4Error}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={c4Form.name}
                      onChange={e => setC4Form(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Category 4 name"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={c4Form.order}
                      onChange={e => setC4Form(prev => ({ ...prev, order: e.target.value }))}
                      min={0}
                      step={1}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeC4Modal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleC4Save}
                  disabled={c4Saving}
                >
                  {c4Saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingC4 ? (
                    'Save Changes'
                  ) : (
                    'Add Category 4'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
